// ─── Certification renewals (ADR 0030, issue #225) ───
// A company-scoped catalog of certification KINDS plus per-person instances carrying the
// dates that matter. The catalog is what makes the team year view and the list filters
// group correctly — free text would degrade every rollup into string matching.

import type { MemberUser } from './user';

/**
 * The system-wide default alert ladder, in days before expiry.
 *
 * Resolution order (ADR 0030 D2), first non-null wins:
 * 1. `CertificationType.leadDaysOverride` — a medical checkup and a cloud certification
 *    do not warrant the same warning.
 * 2. `Workspace.certificationLeadDays` — the workspace default.
 * 3. This constant.
 */
export const DEFAULT_CERTIFICATION_LEAD_DAYS = [90, 30, 7] as const;

/**
 * Derived lifecycle status of a {@link PersonCertification}. Computed SERVER-SIDE so the
 * list, the year view and the expiry timer cannot disagree about what "expiring" means.
 *
 * - `NO_EXPIRY` — `expiresAt` is null. A permanent certification; never alerted on.
 * - `VALID` — expires later than the widest configured lead window.
 * - `EXPIRING` — inside the widest configured lead window, but not yet past.
 * - `EXPIRED` — `expiresAt` is in the past.
 */
export const CERTIFICATION_STATUSES = ['NO_EXPIRY', 'VALID', 'EXPIRING', 'EXPIRED'] as const;

/** One of {@link CERTIFICATION_STATUSES}. */
export type CertificationStatus = (typeof CERTIFICATION_STATUSES)[number];

/**
 * A KIND of certification that can expire ("AWS Solutions Architect", "PRL básico").
 * Company-scoped like Client (ADR 0016): one row the whole company reports on, rather
 * than one per workspace. Managed by workspace managers and superadmins (ADR 0030 D8).
 */
export interface CertificationType {
  /** Unique identifier (UUID v4) */
  id: string;
  /** Owning company (ADR 0016 tenancy) */
  companyId: string;
  /** Display name, unique per company */
  name: string;
  /** Issuing body ("Amazon Web Services"), or null */
  issuer: string | null;
  /**
   * Default renewal period in months, used to prefill `expiresAt` when a person records
   * one. `null` = no fixed cadence; the date is entered by hand.
   */
  validityMonths: number | null;
  /**
   * Per-type override of the alert ladder, in days before expiry. `null` = inherit the
   * workspace default, then {@link DEFAULT_CERTIFICATION_LEAD_DAYS}.
   */
  leadDaysOverride: number[] | null;
  /** Marker colour in the year view (hex) */
  color: string;
  /** Creation timestamp (ISO 8601) */
  createdAt: string;
  /** Last-update timestamp (ISO 8601) */
  updatedAt: string;
  /** Soft-delete timestamp (ISO 8601), or null. Soft-delete = "Archive" in the UI. */
  deletedAt: string | null;
  /** Optimistic-locking version */
  version: number;
}

/**
 * One person holding one certification.
 *
 * There is deliberately NO uniqueness on (userId, typeId): renewal HISTORY is the point,
 * so the 2024 and 2027 instances of the same certification coexist and the year view can
 * show when it was last renewed. "Current" is derived (`max(expiresAt)`), never enforced.
 */
export interface PersonCertification {
  /** Unique identifier (UUID v4) */
  id: string;
  /** The person who holds it */
  userId: string;
  /** Which {@link CertificationType} this is an instance of */
  typeId: string;
  /** Civil date `YYYY-MM-DD` it was obtained, or null */
  obtainedAt: string | null;
  /**
   * Civil date `YYYY-MM-DD` it expires, or null.
   *
   * ⚠️ A civil DATE, not an instant — the column is `@db.Date`. The alert ladder is day
   * arithmetic in the Europe/Madrid frame; computing it on UTC timestamps drifts the whole
   * ladder by a day for half the year (ADR 0030 D5).
   */
  expiresAt: string | null;
  /** Free-text note, or null */
  note: string | null;
  /** Creation timestamp (ISO 8601) */
  createdAt: string;
  /** Last-update timestamp (ISO 8601) */
  updatedAt: string;
  /** Soft-delete timestamp (ISO 8601), or null */
  deletedAt: string | null;
  /** Optimistic-locking version */
  version: number;
}

/** The catalog row as joined onto a person's certification for display. */
export interface CertificationTypeSummary {
  id: string;
  name: string;
  issuer: string | null;
  color: string;
}

/**
 * A {@link PersonCertification} with its holder and catalog row joined, plus the
 * server-derived status — the shape the list and year-view endpoints return.
 */
export interface PersonCertificationDetail extends PersonCertification {
  /** The holder, resolved for display */
  user: MemberUser;
  /** The catalog row, resolved for display */
  type: CertificationTypeSummary;
  /** Server-derived status (see {@link CERTIFICATION_STATUSES}) */
  status: CertificationStatus;
  /**
   * Whole days from today (Europe/Madrid) until `expiresAt`; negative once past,
   * `null` when the certification does not expire.
   */
  daysUntilExpiry: number | null;
  /**
   * True when a later-expiring certification of the SAME type exists for this person —
   * i.e. this row was renewed, and its lapse is history rather than a problem.
   *
   * Without it, an expired-and-renewed 2024 row and a genuinely lapsed one are both just
   * "a marker in the past", and the year view cannot tell the reader which is which.
   * Derived server-side for the same reason as {@link PersonCertificationDetail.status}:
   * the year grid, the list and the timer must never disagree about it. It is NOT
   * derivable client-side — the year query is scoped to one calendar year, so a renewal
   * in the following year is simply absent from the data the grid holds.
   */
  isSuperseded: boolean;
}

/**
 * Bucket counts carried on the certification list response (GHE #927).
 *
 * Computed over the set narrowed by person / type / space but NOT by urgency — a count that
 * shrank as you clicked through the buckets would be useless for deciding which to click.
 *
 * `ladder` mirrors the workspace's configured alert rungs, ascending, so the chips the UI
 * offers are exactly the thresholds that already trigger its reminder emails.
 */
export interface CertificationBucketCounts {
  /** Already past its expiry date. */
  expired: number;
  /** One entry per configured lead-day rung, ascending (default `[7, 30, 90]`). */
  ladder: { days: number; count: number }[];
  /** Everything in scope, urgency ignored — including rows that never expire. */
  all: number;
}
